import { DAEMON_URL } from "../config";
import { HttpError } from "./errors";
import type { ConversationRow, ConversationSummary, TurnRow } from "./types";

async function doFetch(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) throw new HttpError(res.status, `http ${res.status}`);
  return res;
}

export async function listConversations(
  project: string,
): Promise<ConversationSummary[]> {
  const headers = { "Accept": "application/json" };
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}`;
  const res = await doFetch(url, { method: "GET", headers, credentials: "include" });
  const body = (await res.json()) as { conversations: ConversationSummary[] };
  return body.conversations;
}

export async function getTurns(
  project: string,
  conversationId: number,
): Promise<TurnRow[]> {
  const headers = { "Accept": "application/json" };
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${encodeURIComponent(String(conversationId))}/turns`;
  const res = await doFetch(url, { method: "GET", headers, credentials: "include" });
  const body = (await res.json()) as { turns: TurnRow[] };
  return body.turns;
}

export async function startNewConversation(
  project: string,
): Promise<ConversationRow> {
  const headers = { "Accept": "application/json" };
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}`;
  const res = await doFetch(url, { method: "POST", headers, credentials: "include" });
  const body = (await res.json()) as { conversation: ConversationRow };
  return body.conversation;
}

/** Phase 4: clone someone else's conversation (typically the bot's) into a
 * fresh conversation owned by the calling user. The cloned conversation is
 * set active on the daemon so the next /chat message lands inside it. */
export async function copyConversation(
  project: string,
  sourceId: number,
): Promise<{ conversation: ConversationRow; turnsCopied: number }> {
  const url = `${DAEMON_URL}/conversations/${encodeURIComponent(project)}/${sourceId}/copy`;
  const res = await doFetch(url, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  return (await res.json()) as { conversation: ConversationRow; turnsCopied: number };
}
